import { LabelType } from "@angular-slider/ngx-slider";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AuctionStatus, AuctionType, AuctionTypeType, SessionKey } from "@shared/clickar-constants";
import { AdvancedSearchDTO, AuctionDTO } from "@shared/model/auction.model";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { ApiService } from "@shared/services/api.service";
import { AuctionService } from "@shared/services/auction/auction.service";
import { SnackbarService } from "@shared/services/snackbar.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UsersService } from "@shared/services/users/users.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { Subscription } from "rxjs";

@Component({
	selector: "app-auctions",
	templateUrl: "./auctions.component.html",
	styleUrl: "./auctions.component.scss",
	standalone: false
})
export class AuctionsComponent implements OnInit, OnDestroy {
	wcmService = inject(WcmService);
	auctionService = inject(AuctionService);
	router = inject(Router);
	dialog = inject(MatDialog);
	utils = inject(ClickarUtilsService);
	usersService = inject(UsersService);
	translate = inject(TranslateService);
	apiService = inject(ApiService);
	storageService = inject(StorageManagerService);

	private snackBarService = inject(SnackbarService);

	private navigationSubscription: Subscription;

	/** Used to store the not-filtered auctions, to be shown on reset */
	private leasysAuctions: AuctionDTO[] = [];
	/** Used to store the not-filtered auctions, to be shown on reset */
	private multiAuctions: AuctionDTO[] = [];
	/** Used to store the not-filtered auctions, to be shown on reset */
	private otherAuctions: AuctionDTO[] = [];

	/** Contains the auctions to be shown: it always corresponds to {@link leasysAuctions } unless a search has been triggered */
	protected filteredLeasysAuctions: AuctionDTO[] | null = null;
	/** Contains the auctions to be shown: it always corresponds to {@link multiAuctions } unless a search has been triggered */
	protected filteredMultiAuctions: AuctionDTO[] | null = null;
	/** Contains the auctions to be shown: it always corresponds to {@link otherAuctions } unless a search has been triggered */
	protected filteredOtherAuctions: AuctionDTO[] | null = null;

	// Advanced search
	protected advancedSearchView = false;
	protected readonly kmRange = [0, 2e5] as [number, number];
	protected readonly kmStep = 5000 as const;
	protected readonly registrationYearRange = [2000, new Date().getFullYear()] as [number, number];
	/** Keeps track of whether last advanced search had been saved */
	protected savedSearch = false;

	protected getKmLabel = (value: number, label: LabelType): string => (label === LabelType.Ceil || (label === LabelType.High && value === this.kmRange[1])) ? `${value}+` : `${value}`;

	private getDefaultAdvancedSearch = () => ({
		brand: "",
		model: "",
		kmFrom: this.kmRange[0],
		kmTo: this.kmRange[1],
		registrationYearFrom: this.registrationYearRange[0],
		registrationYearTo: this.registrationYearRange[1]
	});
	protected previousAdvancedSearchValue: AdvancedSearchDTO = this.getDefaultAdvancedSearch();
	/** Tells whether an advanced search has been triggered (and succeeded) */
	protected advancedSearchTriggered = false;
	protected advancedSearchForm = new FormGroup({
		model: new FormControl<string>(this.previousAdvancedSearchValue.model!, { nonNullable: true }), // "!" just to satisfy Typescript's quirks
		brand: new FormControl<string>(this.previousAdvancedSearchValue.brand!, { nonNullable: true }), // "!" just to satisfy Typescript's quirks
		registrationYear: new FormControl<[number, number]>(this.registrationYearRange, { nonNullable: true }),
		km: new FormControl<[number, number]>(this.kmRange, { nonNullable: true })
	});

	protected formUntouched = () => {
		const values = this.advancedSearchForm.getRawValue();
		return this.previousAdvancedSearchValue.brand === values.brand.trim() &&
			this.previousAdvancedSearchValue.model === values.model.trim() &&
			this.previousAdvancedSearchValue.kmFrom === values.km[0] &&
			this.previousAdvancedSearchValue.kmTo === values.km[1] &&
			this.previousAdvancedSearchValue.registrationYearFrom === values.registrationYear[0] &&
			this.previousAdvancedSearchValue.registrationYearTo === values.registrationYear[1];
	};

	randomVehicles?: { [p: string]: VehicleDTO[] };

	// user: UserDTO;
	protected market = "";

	showAuctionType = false;

	runningCount = 0;
	scheduledCount = 0;

	ngOnInit() {
		this.navigationSubscription = this.router.events.subscribe((e: any) => {
			// If it is a NavigationEnd event re-initalise the component
			if (e instanceof NavigationEnd) {
				this.refresh();
				this.resetSearch();
			}
		});

		this.refresh();

		if (!(this.market = this.storageService.getSessionItem(SessionKey.USER_MARKET, ""))) {
			this.usersService.getProfile()
				.subscribe(value => {
					if (value) {
						this.market = value.idMarket;
					}
				});
		}
	}

	refresh() {
		this.apiService.getRunningAuctionsUsingGET()
			.subscribe(auctions => {
				this.runningCount = auctions.filter(a => a.status === AuctionStatus.RUNNING).length;
				this.scheduledCount = auctions.filter(a => a.status === AuctionStatus.SCHEDULED).length;

				this.filterAuctions(auctions, false);
			});
	}

	private filterAuctions(auctions: AuctionDTO[], filtered = true) {
		const sortMethod = (a: AuctionDTO, b: AuctionDTO) => a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0,
			auctionGroups: { [key in AuctionTypeType]: AuctionDTO[] } = {
				[AuctionType.LEASYS]: [],
				[AuctionType.MULTI]: [],
				[AuctionType.OTHER]: []
			};
		this.showAuctionType = auctions.some(a => a.type === "BLIND");
		for (const auction of auctions.sort((a, b) => a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0)) {
			auctionGroups[auction.auctionType].push(auction);
		}

		this.filteredLeasysAuctions = auctionGroups[AuctionType.LEASYS].sort(sortMethod);
		this.filteredMultiAuctions = auctionGroups[AuctionType.MULTI].sort(sortMethod);
		this.filteredOtherAuctions = auctionGroups[AuctionType.OTHER].sort(sortMethod);
		if (!filtered) { // not searching, we store the data in order to reset
			this.leasysAuctions = this.filteredLeasysAuctions;
			this.multiAuctions = this.filteredMultiAuctions;
			this.otherAuctions = this.filteredOtherAuctions;

			if (auctions.length) {
				const auctionIds = auctions.map(value1 => value1.idAuction);
				this.apiService.getRandomVehicleUsingGET(auctionIds)
					.subscribe(value1 => {
						this.randomVehicles = value1;
					});
			}
		}
	}

	ngOnDestroy() {
		if (this.navigationSubscription) {
			this.navigationSubscription.unsubscribe();
		}
	}

	/*
	goToDetails(idAuction: number) {
		if (idAuction) {
			this.router.navigate(
				[`${ClickarPath.PRIVATE}/${ClickarPath.AUCTION_DETAILS}`],
				{
					queryParams: { idAuction }
				}
			);
		}
	}

	getVehicle = (idAuction: number) => this.randomVehicles ? this.randomVehicles[idAuction] : null;

	goToShowcase() {
		this.router.navigate([`${ClickarPath.PRIVATE}/${ClickarPath.SHOWCASE_MANAGEMENT}/${ClickarPath.SHOWCASE_CATALOGUE}`]);
	}
	*/

	/** Latest simple search string, acting both as string and flag for a triggered simple search. */
	protected previousSearchValue = "";

	protected search(value: string) {
		value = value.trim();
		if (this.advancedSearchTriggered || this.previousSearchValue !== value) {
			this.advancedSearchTriggered = false;
			this.previousSearchValue = value; // TODO: a seconda di come si gestisce l'errore, questo potrebbe andare dopo il success della richiesta, perché altrimenti potremmo avere dati che non corrispondono col recap della ricerca

			if (value) { // If there is a search string, we trigger a new call
				this.auctionService.searchInRunningAndScheduledAuctionsUsingGET(value)
					.subscribe(auctions => {
						this.filterAuctions(auctions);
						/* if (!auctions.length) {
								const dialogConfig = new MatDialogConfig(),
								data: AlertMessage = new AlertMessage();
								data.successMsg = 'success.noVehicleFound.msg';
								dialogConfig.data = data;
								dialogConfig.width = "auto";
								// dialogConfig.height = '250px';
								let dialogRef = this.dialog.open(SuccessDialogComponent, dialogConfig);
								} */
					}); // TODO: cosa fare in caso di errore?
			}
			else { // Otherwise, we reset the original data avoiding triggering a call
				this.resetSearch();
			}
		}
	}

	/* TODO: chi fa apparire il toast nella chiamata? */

	/** Tells whether the submitted data corresponds to landing search form settings */
	private itsBaseAdvancedSearch = (data: AdvancedSearchDTO) =>
		data.brand === "" &&
		data.model === "" &&
		data.registrationYearFrom === this.registrationYearRange[0] &&
		data.registrationYearTo === this.registrationYearRange[1] &&
		data.kmFrom === this.kmRange[0] &&
		data.kmTo === this.kmRange[1];

	protected submitAdvancedSearch() {
		const values = this.advancedSearchForm.getRawValue(),
			callData: AdvancedSearchDTO = {
				brand: values.brand.trim(),
				model: values.model.trim(),
				registrationYearFrom: values.registrationYear[0],
				registrationYearTo: values.registrationYear[1],
				kmFrom: values.km[0]
			},
			// We don't know the maximum of possible mileage, hence in case it's set to the empirical maximum of values.registrationYear[1] we don't set any cap, in order to avoid missing the higher values
			completeData = Object.assign({ kmTo: values.km[1] }, callData);

		if (this.previousSearchValue || !this.itsBaseAdvancedSearch(callData)) { // If we are overriding a simple search or data is different than initial, we trigger a new call
			this.previousSearchValue = "";
			this.previousAdvancedSearchValue = completeData; // TODO: a seconda di come si gestisce l'errore, questo potrebbe andare dopo il success della richiesta, perché altrimenti potremmo avere dati che non corrispondono col recap della ricerca
			this.advancedSearchTriggered = true;
			this.savedSearch = false;

			this.auctionService.advancedSearchInRunningAndScheduledAuctions(values.km[1] !== this.kmRange[1] ? completeData : callData).subscribe(auctions => {
				this.filterAuctions(auctions);
			}); // TODO: cosa fare in caso di errore?

		} else { // Otherwise, we reset the original data avoiding triggering a call
			this.resetSearch();
		}
	}

	protected resetSearch() {
		this.savedSearch = false;
		this.previousSearchValue = "";
		this.advancedSearchTriggered = false;
		this.previousAdvancedSearchValue = this.getDefaultAdvancedSearch();
		this.filteredLeasysAuctions = this.leasysAuctions;
		this.filteredMultiAuctions = this.multiAuctions;
		this.filteredOtherAuctions = this.otherAuctions;
	}

	protected saveSearch() {
		this.apiService.saveSearch(this.previousAdvancedSearchValue).subscribe(() => {
			this.savedSearch = true;
			this.snackBarService.show({

				titlePlaceholder: "auction.search.saveSearchConfirmTitle",
				messagePlaceholder: "auction.search.saveSearchConfirmText"
			}, 15e3);
		})
	}

	refreshSearchByRemoving = (whichOne: "brand" | "model" | "km" | "registrationYear") => {
		// TODO: anche qui, in base a come vengono gestiti gli errori, potrebbe essere necessario aggiornare il previousSearch. Se lo facesse dopo la chiamata e la chiamata si rompesse, resterebba uno stato "rotto"
		this.advancedSearchForm.controls[whichOne].reset();
		this.submitAdvancedSearch();
	}
}